import {property, query} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {Nullable} from '#lit/types/general.js';
import type {Constructor} from '#lit/types/classes.js';
import type {PointTuple} from '#lit/lib/point.js';
import {Point} from '#lit/lib/point.js';
import {enforceIntoRange} from '#lit/utils/numbers.js';
import type {Model} from './architecture.js';
import {Msg, main} from './architecture.js';
import {v4} from 'uuid';
import type FlyCanvas from '../canvas/index.js';
import styles from './style.scss';
import {CanvasZIndex} from '#lit/types/canvas.js';

export const CanvasElementEventAction = {
  Attached: 'attached',
  Selected: 'selected',
  Deselected: 'deselected',
  Moved: 'moved',
  Destroyed: 'destroyed',
};
export type CanvasElementEventAction =
  (typeof CanvasElementEventAction)[keyof typeof CanvasElementEventAction];

export interface CanvasElementEventDetail {
  element: CanvasElement;
  action: CanvasElementEventAction;
}

/**
 * A base canvas element to be used
 */
export default class CanvasElement extends ImmutableComponent<
  Constructor<FlywheelComponent>,
  Model,
  Msg
>({component: FlywheelComponent, main}) {
  static selectedNodeZindex = CanvasZIndex.SELECTED_NODE;

  @property({type: Array}) initialPosition: PointTuple = [0, 0];
  @property({type: Boolean}) selected = false;
  @property({type: Boolean}) disabled = false;
  @property() key = v4();

  @query('.canvas-node')
  $node?: Element;

  $canvas?: FlyCanvas;

  static get styles() {
    return [styles];
  }

  static isInPath(path: EventTarget[]) {
    return !!path.find(el => {
      return el instanceof CanvasElement;
    });
  }

  createEvent(
    action: CanvasElementEventAction
  ): CustomEvent<CanvasElementEventDetail> {
    return new CustomEvent(action, {
      detail: {
        element: this,
        action,
      },
    });
  }

  attach(canvas: FlyCanvas) {
    this.$canvas = canvas;
    this.handleUpdate(Msg.setAttached(true));

    this.dispatchEvent(this.createEvent('attached'));
  }

  move(by: Point | PointTuple) {
    by = by instanceof Point ? by : Point.fromTuple(by);
    this.handleUpdate(Msg.changePosition(by));
    return this;
  }

  focus() {
    super.focus();
    this.handleUpdate(Msg.focus());
  }

  unfocus() {
    this.handleUpdate(Msg.unfocus());
  }

  select() {
    this.handleUpdate(Msg.select());
    this.dispatchEvent(this.createEvent('selected'));

    return this;
  }

  deselect() {
    this.handleUpdate(Msg.deselect());
    this.dispatchEvent(this.createEvent('deselected'));

    return this;
  }

  disable() {
    return this;
  }

  delete() {
    this.dispatchEvent(this.createEvent(CanvasElementEventAction.Destroyed));
    this.remove();
  }

  root(): Nullable<HTMLElement> {
    return this;
  }

  isInPath(path: EventTarget[]) {
    return !!path.find(el => {
      return el instanceof Element && el.isSameNode(this);
    });
  }

  updateZIndex() {
    CanvasElement.selectedNodeZindex++;
    this.style.zIndex = CanvasElement.selectedNodeZindex.toString();
  }

  socketOffset(): Point {
    if (!this.$canvas) return Point.from(0, 0);

    const container = this.$node;
    const rect = container?.getBoundingClientRect() ?? {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    };

    const mouseX = this.$canvas?.cursor.globalPosition.x ?? 0;
    const planeRect = this.$canvas?.model.planeRect ?? {x: 0, y: 0};
    const scale = this.$canvas?.model.scale ?? 1;

    const posX = enforceIntoRange(rect.left, mouseX, rect.right);
    const posY = rect.top;

    const x = (posX - planeRect.x) / scale;
    const y = (posY - planeRect.y) / scale;

    return Point.from(x, y);
  }
}
